// utils/printDrugClassReport.js
// Reusable print utility for Generic Drug Coverage (Drug Class Search) page.

export function printDrugClassReport({
  selectedPlanDetails = {},
  drugCostNote = '',
  showInsulinAlert = false,
  // IDs in the DOM you want to include
  sectionSelectors = [
    '#drug-class-plan-summary',
    '#drug-class-retail-note',
    '#drug-class-results-table',
  ],
  includeColumnExplanations = false,
  includeDisclaimer = true,
  customTitle = ' Drug Coverage and Cost Report',
  reportYear = new Date().getFullYear(),

  /* ---------- NEW PARAMETERS (all optional) ---------- */
  appendixItems = [],          // [{ drugKey, drugName, paHtml, monthlyBreakdownHtml }]
  includeAppendix = true,      // master switch to render appendix
  includeAppendixHeading = true,
  forceAppendixNewPage = true, // start appendix on new page
  appendixTitle = 'Appendix – Detailed PA Criteria & Monthly Breakdowns',
  sanitizeAppendix = true,     // sanitize user‑generated fragments
}) {
  // Collect existing nodes
  const existing = sectionSelectors
    .map(sel => document.querySelector(sel))
    .filter(Boolean);

  if (!existing.length) return;

  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.sandbox.add('allow-same-origin','allow-scripts','allow-modals');
  document.body.appendChild(frame);

  frame.onload = () => {
    const win = frame.contentWindow;
    const doc = frame.contentDocument;
    if (!win || !doc) return;

    // Copy current head to inherit AntD styles, etc.
    doc.head.innerHTML = document.head.innerHTML;

    const style = doc.createElement('style');
    style.textContent = `
      @page {
        size: Letter portrait;
        margin: 18mm 18mm 22mm 18mm;
      }

      html, body {
        margin:0 !important;
        padding:0 !important;
        width:100%;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;
        font-size:12px;
        line-height:1.38;
        color:#111;
      }

      .report-wrapper {
        padding:0 10mm 10mm 6mm;
        box-sizing:border-box;
      }

      .report-header {
        border-bottom:2px solid #1d5fa7;
        padding:6px 0 10px;
        margin:0 0 8px;
      }
      .header-flex {
        display:flex;
        align-items:center;
        gap:18px;
      }
      .logo-box img {
        height:54px;
        width:auto;
        display:block;
      }
      .title-box h1 {
        font-size:27px;
        margin:0 0 6px;
        font-weight:700;
        letter-spacing:.5px;
        line-height:1.15;
        color:#0d2f4a;
      }
      .report-meta {
        font-size:13px;
        color:#444;
        margin:0 0 4px;
        font-weight:500;
      }
      .report-tagline {
        font-size:11.5px;
        color:#666;
      }

    /* Page sections. Keep them tight; no fake spacers. */
    .page-block{
    margin: 6mm 0 0 0;        /* one clear top margin */
    padding: 0;
    break-inside: auto;       /* allow tables to split */
    display: flow-root;       /* prevent margin collapse */
    }
    .report-wrapper > .page-block:first-of-type{
    margin-top: 2mm;
    }

    /* Kill extra space between summary & table */
    #drug-class-plan-summary{ margin:0 0 3mm !important; }
    #drug-class-plan-summary p,
    #drug-class-plan-summary div{ margin:1px 0 !important; }
    #drug-class-retail-note,
    #drug-class-results-table{ margin-top:1.5mm !important; }

      /* Plan summary formatting (mirrors enhancements) */
      #drug-class-plan-summary {
        font-size:15px;
        line-height:1.5;
        font-weight:500;
        word-break:keep-all;
        overflow-wrap:anywhere;
      }
      #drug-class-plan-summary p,
      #drug-class-plan-summary div {
        margin:4px 0 0;
        font-size:15px;
      }
      #drug-class-plan-summary strong {
        font-size:16px;
        color:#0d3a66;
      }

      h3 {
        font-size:17px;
        font-weight:600;
        margin:24px 0 10px;
        color:#14375a;
        border-bottom:1px solid #d9dfe5;
        padding-bottom:5px;
        page-break-after:avoid;
      }

      table, .ant-table table {
        width:100% !important;
        border-collapse:collapse !important;
      }
      .ant-table, .ant-table-container {
        border:1px solid #c7d2dd !important;
      }
      .ant-table-thead > tr > th {
        background:#f1f5f9 !important;
        color:#17324d !important;
        font-weight:600;
        border:1px solid #c7d2dd !important;
        padding:7px 8px !important;
        font-size:12px;
      }
      .ant-table-tbody > tr > td {
        border:1px solid #d4dce3 !important;
        padding:6px 7px !important;
        font-size:11.8px;
        vertical-align:top;
      }
      .ant-table-tbody > tr:nth-child(odd) > td {
        background:#fafcff !important;
      }

      .highlight-column {
        background:#fff7e6 !important;
        position:relative;
      }
      .ant-table-thead .highlight-column {
        background:#ffe7c2 !important;
      }
      .highlight-column::before {
        content:"";
        position:absolute;
        top:0; bottom:0; left:0;
        width:3px;
        background:#ffa94d;
      }

      .currency-cell {
        text-align:right;
        font-variant-numeric:tabular-nums;
        white-space:nowrap;
      }
        /* inside your <style> injection in printM3PReport */
        #m3p-section-chart svg {
        width: 100% !important;
        height: 360px !important;
        margin: 0 auto;
        }

      .report-notes ul {
        margin:6px 0 0 18px;
        padding:0;
        font-size:11.5px;
        line-height:1.45;
      }
      .report-notes li { margin:0 0 5px; }
      .report-notes strong { color:#14375a; }

      .report-disclaimer {
        font-size:10.5px;
        color:#555;
        margin-top:24px;
        border-top:1px solid #d9dfe5;
        padding-top:8px;
      }

      .page-block::before{ content:none !important; }

      .print-hide,
      button, .ant-btn,
      .ant-radio-group, .ant-form-item-control-input-content > .ant-select,
      .ant-picker, .ant-modal-root,
      .floating-help-button {
        display:none !important;
      }

      /* ---------- NEW: Appendix Styling ---------- */
      #drug-class-appendix {
        /* container for appendix, spacing handled via page-block */
      }
      .appendix-header {
        font-size:19px;
        font-weight:700;
        margin:0 0 14px;
        color:#0d2f4a;
      }
      .appendix-item {
        margin:0 0 14px;
        page-break-inside:auto;
      }
      .appendix-item h4 {
        font-size:15px;
        margin:0 0 6px;
        color:#14375a;
      }
      .appendix-subsection-title {
        font-size:13px;
        font-weight:600;
        margin:10px 0 4px;
        color:#0d3a66;
      }
      .appendix-box {
        font-size:12.5px;
        line-height:1.5;
        background:#fff;
        border:1px solid #d9e2ec;
        border-radius:4px;
        padding:8px 10px;
        overflow:hidden;
      }
      .appendix-box table {
        width:100%;
        border-collapse:collapse;
        margin-top:4px;
      }
      .appendix-box table td,
      .appendix-box table th {
        border:1px solid #d4dce3;
        padding:4px 6px;
        font-size:12px;
        vertical-align:top;
      }
      .appendix-box a {
        color:#0a4f8a;
        text-decoration:underline;
        word-break:break-word;
      }
        /* hide anything with .no-print when printing */
        .no-print {
        display: none !important;
        }
     /* Footer shown on EVERY page in Chromium */
        @media print {
        body{
            counter-reset: page;
            /* Reserve space so content never overlaps footer */
            padding-bottom: 26mm !important;
        }
        .page-footer{
            position: fixed;
            left: 0; right: 0;
            bottom: 8mm;                    /* inside @page bottom margin */
            text-align: center;
            font-size: 10px;
            color: #555;
            z-index: 9999;
        }
        .page-footer::after{
            content: " Page " counter(page);
        }
        }

    `;
    doc.head.appendChild(style);

    // Build wrapper & header
    const wrapper = doc.createElement('div');
    wrapper.className = 'report-wrapper';
    doc.body.appendChild(wrapper);

    const header = doc.createElement('div');
    header.className = 'report-header';
    header.innerHTML = `
      <div class="header-flex">
        <div class="logo-box">
          <img src="/Logo_favicon_blackbg.png" alt="MedHug Logo" />
        </div>
        <div class="title-box">
          <h1>${customTitle}</h1>
          <div class="report-meta">
            Generated ${new Date().toLocaleString()} &nbsp;|&nbsp;
            ${selectedPlanDetails?.plan_name ? `Plan: ${escapeHTML(selectedPlanDetails.plan_name)} &nbsp;|&nbsp;` : ''} Year: ${reportYear}
          </div>
          <div class="report-tagline">All costs are estimates for reference.</div>
        </div>
      </div>
    `;
    wrapper.appendChild(header);

    // Clone user sections
    existing.forEach((node, idx) => {
        const cloned = node.cloneNode(true);
        if (idx > 0) cloned.classList.add('page-block');

      // Remove interactive controls inside
      cloned.querySelectorAll(
        'button,.ant-btn,.ant-select-selector,.ant-select-arrow,.ant-radio-group,' +
        '.ant-form-item-control-input-content > *:not(table)'
      ).forEach(n => n.remove());

      if (cloned.matches('#drug-class-results-table')) {
        cloned.querySelectorAll('span[role="img"], .ant-table-row-expand-icon, .action-buttons, .print-hide')
          .forEach(n => n.remove());
      }

      wrapper.appendChild(cloned);
    });

    // Column Explanations
    if (includeColumnExplanations) {
      const notes = doc.createElement('div');
      notes.className = 'report-notes page-block';
      notes.id = 'drug-class-column-explanations';
      notes.innerHTML = `
        <h3>Column Explanations</h3>
        <ul>
          <li><strong>Drug Name / Strength:</strong> Distinct covered product name (may aggregate multiple NDCs).</li>
          <li><strong>Tier:</strong> Plan formulary tier; impacts cost-share (lower is typically cheaper).</li>
          <li><strong>Restrictions:</strong> Prior Authorization (PA), Step Therapy (ST), Quantity Limits (QL), etc.</li>
          <li><strong>Preferred Cost:</strong> Estimated patient cost at a preferred pharmacy (if applicable).</li>
          <li><strong>Standard Cost:</strong> Estimated patient cost at a standard in‑network pharmacy.</li>
          <li><strong>Notes / PA Details:</strong> Additional plan messaging or utilization criteria.</li>
        </ul>
      `;
      wrapper.appendChild(notes);
    }

    /* ---------- NEW: Appendix Injection ---------- */
    if (includeAppendix && appendixItems && appendixItems.length) {
      const cleanItems = appendixItems
        .map((it, idx) => {
          const safePA = it.paHtml ? (sanitizeAppendix ? sanitizeAppendixHTML(it.paHtml) : it.paHtml) : '';
          const safeBreak = it.monthlyBreakdownHtml || '';
          if (!safePA && !safeBreak) return null;
          return {
            index: idx,
            drugKey: it.drugKey || `drug-${idx}`,
            drugName: it.drugName || it.drugKey || `Drug ${idx + 1}`,
            pa: safePA,
            breakdown: safeBreak
          };
        })
        .filter(Boolean);

      if (cleanItems.length) {
        const appendix = doc.createElement('div');
        appendix.id = 'drug-class-appendix';
        appendix.className = 'page-block';
        if (forceAppendixNewPage && wrapper.offsetHeight % 800 > 500) {
            appendix.style.pageBreakBefore = 'always';
        }

        if (includeAppendixHeading) {
          appendix.innerHTML = `
            <div class="appendix-header">${escapeHTML(appendixTitle)}</div>
            <div style="font-size:11.5px;color:#555;margin-bottom:12px;">
              Detailed optional information (Prior Authorization criteria and/or Monthly Breakdowns) for selected drugs.
            </div>
          `;
        }

        cleanItems.forEach((item, i) => {
          const block = doc.createElement('div');
            block.className = 'appendix-item';
          const refCode = `A.${i + 1}`;
          block.innerHTML = `
            <h4>${refCode} – ${escapeHTML(item.drugName)}</h4>
            ${
              item.pa
                ? `<div class="appendix-subsection-title">Prior Authorization Details</div>
                   <div class="appendix-box">${item.pa}</div>`
                : ''
            }
            ${
              item.breakdown
                ? `<div class="appendix-subsection-title" style="${item.pa ? 'margin-top:10px;' : ''}">Monthly Breakdown</div>
                   <div class="appendix-box">${item.breakdown}</div>`
                : ''
            }
          `;
          appendix.appendChild(block);
        });

        wrapper.appendChild(appendix);
      }
    }
    /* ---------- END Appendix Injection ---------- */

    if (includeDisclaimer) {
      const disclaimer = doc.createElement('div');
      disclaimer.className = 'report-disclaimer page-block';
      disclaimer.id = 'drug-class-disclaimer';
      disclaimer.innerHTML = `
        <strong>Disclaimer:</strong> Figures shown are estimates and may differ from actual pharmacy claims.
        Coverage, deductible application, and patient responsibility depend on plan adjudication rules.
        These projections are based on plan-specific information, not patient‑specific clinical or claims data.
      `;
      wrapper.appendChild(disclaimer);
    }

    const footer = doc.createElement('div');
    footer.className = 'page-footer';
    footer.innerHTML = `© ${new Date().getFullYear()} MedHug – ${customTitle}`;
    doc.body.appendChild(footer);   // Chrome will duplicate fixed elems per page

    win.onafterprint = win.onbeforeunload = () => frame.remove();
    setTimeout(() => { win.focus(); win.print(); }, 60);
  };

  frame.srcdoc = '<!doctype html><html><head></head><body></body></html>';
}

/* Simple HTML escaper for plan names etc. */
function escapeHTML(str = '') {
  return String(str)
    .replace(/&/g,'&amp;')
    .replace(/</g,'&lt;')
    .replace(/>/g,'&gt;')
    .replace(/"/g,'&quot;')
    .replace(/'/g,'&#39;');
}

/* ---------- NEW: Appendix Sanitizer (keeps basic formatting) ---------- */
function sanitizeAppendixHTML(html) {
  if (!html) return '';
  const template = document.createElement('template');
  template.innerHTML = String(html);

  const allowed = new Set([
    // HTML…
    'DIV','P','SPAN','STRONG','B','EM','I','UL','OL','LI','BR',
    'TABLE','THEAD','TBODY','TR','TD','TH','A','H1','H2','H3','H4','H5','H6',
    // plus SVG…
    'SVG','G','PATH','CIRCLE','RECT','LINE','POLYLINE','POLYGON','TEXT','TSPAN','DEFS','LINEARGRADIENT','STOP'
  ]);

  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_ELEMENT, null);
  const toRemove = [];

  while (walker.nextNode()) {
    const el = walker.currentNode;
    if (!allowed.has(el.tagName)) {
      toRemove.push(el);
      continue;
    }
    [...el.attributes].forEach(attr => {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on') || ['style','srcdoc','data','formaction'].includes(name)) {
        el.removeAttribute(attr.name);
      } else if (name === 'href' && /^javascript:/i.test(attr.value)) {
        el.removeAttribute('href');
      }
    });
  }
  toRemove.forEach(el => el.remove());
  return template.innerHTML;
}
